import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { SEARCE_COLORS } from '../../theme';

/** Rounded KPI strip radius (Utilization Insights and aligned dashboard pages). */
export const METRIC_CARD_APPLE_RADIUS = '18px';

/** Shared KPI title typography (all dashboard metric strips). */
export const METRIC_CARD_TITLE_FONT_SIZE = '13px';
export const METRIC_CARD_TITLE_LINE_HEIGHT = 1.1;
/** MUI spacing units — gap between title and value. */
export const METRIC_CARD_TITLE_MARGIN_BOTTOM = 0.25;

export const METRIC_CARD_APPLE_EASE = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

export function getMetricCardAppleShadows() {
  return {
    default: `0 1px 2px ${alpha('#000', 0.06)}, 0 4px 14px ${alpha('#000', 0.1)}`,
    hover: `0 2px 6px ${alpha('#000', 0.08)}, 0 8px 22px ${alpha('#000', 0.14)}`,
  };
}

/** Nested hover-only rules merged into `metricCardAppleShellSx`. */
export type MetricCardAppleHoverExtras = SystemStyleObject<Theme>;

/** Shared curved shell for colored metric cards (Projects, DRI, etc.). */
export function metricCardAppleShellSx(
  hoverBg: string,
  extraHover?: MetricCardAppleHoverExtras
): SystemStyleObject<Theme> {
  const shadows = getMetricCardAppleShadows();
  return {
    borderRadius: METRIC_CARD_APPLE_RADIUS,
    border: 'none',
    boxShadow: shadows.default,
    transition: `transform 0.35s ${METRIC_CARD_APPLE_EASE}, box-shadow 0.35s ${METRIC_CARD_APPLE_EASE}, background 0.35s ${METRIC_CARD_APPLE_EASE}`,
    '&:hover': {
      transform: 'translateY(-2px) scale(1.01)',
      borderColor: 'transparent',
      background: hoverBg,
      boxShadow: shadows.hover,
      ...extraHover,
    },
    '&:focus-visible': {
      outline: `2px solid ${alpha('#fff', 0.45)}`,
      outlineOffset: '3px',
    },
  };
}

/** Background, on-surface text, and hover background for a metric card shell. */
export interface ColorScheme {
  bg: string;
  text: string;
  hover: string;
}

/**
 * Maps a theme color key (or `"gray"`) to concrete metric card colors.
 * Unknown keys resolve to a neutral surface so cards never render unstyled.
 *
 * @param colorKey - Typically a `SEARCE_COLORS.*` token or `"gray"`.
 * @returns Background, text, and hover colors for the card.
 */
export function getColorScheme(colorKey: string): ColorScheme {
  switch (colorKey) {
    case SEARCE_COLORS.primary:
      return {
        bg: SEARCE_COLORS.primary_600,
        text: SEARCE_COLORS.on_primary,
        hover: SEARCE_COLORS.primary_700,
      };
    case SEARCE_COLORS.success:
      return {
        bg: SEARCE_COLORS.success_600,
        text: SEARCE_COLORS.on_success,
        hover: SEARCE_COLORS.success_700,
      };
    case SEARCE_COLORS.warning:
      return {
        bg: SEARCE_COLORS.warning_600,
        text: SEARCE_COLORS.on_warning,
        hover: SEARCE_COLORS.warning_700,
      };
    case SEARCE_COLORS.error:
      return {
        bg: SEARCE_COLORS.error_600,
        text: SEARCE_COLORS.on_error,
        hover: SEARCE_COLORS.error_700,
      };
    case SEARCE_COLORS.info:
      return {
        bg: SEARCE_COLORS.info_600,
        text: SEARCE_COLORS.on_info,
        hover: SEARCE_COLORS.info_700,
      };
    case 'gray':
      return {
        bg: SEARCE_COLORS.neutral_600,
        text: SEARCE_COLORS.on_neutral_800,
        hover: SEARCE_COLORS.neutral_700,
      };
    default:
      return {
        bg: SEARCE_COLORS.neutral_100,
        text: SEARCE_COLORS.on_neutral_100,
        hover: SEARCE_COLORS.neutral_200,
      };
  }
}

/**
 * Shared entrance animation for metric cards (scoped keyframe name avoids clashes).
 */
export const metricCardEntranceSx: SystemStyleObject<Theme> = {
  animation: 'metricCardFadeInUp 0.6s ease-out',
  '@keyframes metricCardFadeInUp': {
    '0%': { opacity: 0, transform: 'translateY(20px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' },
  },
};

/**
 * Short USD string for large dashboard amounts (e.g. `$1.2M`, `$450.0K`).
 *
 * @param value - Raw numeric amount.
 * @returns Formatted string with `$` prefix.
 */
export function formatCompactUsd(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
}
